import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import {
  eventAlreadyExists,
  findAlternativeStretch,
  findNewStretchIndexByAffectedStops,
  isRelevantStretch,
  makeStretchStops,
  shouldFlipStretchDirection,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/changedRouteUtils";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import { EitherTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { useRouteChangedEvents } from "features/CenterContent/VehicleDetails/TrainDetails/useRouteChangedEvents";
import {
  ALTERNATIVE_STRETCHES,
  formatStretchName,
  Stretch,
} from "features/CenterContent/shared/operationalInformation/alternativeStretches";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, Stack, Text, VStack } from "shared/components/Spor";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";

export const TrainChangedRoute: FC<
  CommonTrainInfoFormProps & CommonSubTypeProps & TrainContext
> = ({ trainId, uuid, trainFormPrefix }) => {
  const { t } = useTranslation();

  const { getValues, setValue, formState } =
    useFormContext<EitherTrainFormSchema>();
  const { errors } = formState;

  // Define all field paths
  const originalStretchNameFieldPath =
    `${trainFormPrefix}.originalRouteName` as const;
  const newStretchNameFieldPath = `${trainFormPrefix}.newRouteName` as const;
  const affectedStopsFieldPath = `${trainFormPrefix}.affectedStops` as const;
  const cancelledStopsFieldPath = `${trainFormPrefix}.cancelledStops` as const;
  const newRouteFieldPath = `${trainFormPrefix}.newRoute` as const;

  const { useTrainRouteBatch } = useTrainInfoContext();
  const { data: trainRoute, status: fetchStatus } = useTrainRouteBatch(trainId);

  const { data: existingEvents } = useRouteChangedEvents(trainId);

  const calculateNewStretchIndex = () => {
    const affectedStops = getValues(affectedStopsFieldPath) ?? [];
    return findNewStretchIndexByAffectedStops(affectedStops);
  };

  // Handle internal state for select
  const [newStretchIndex, setNewStretchIndex] = useState<number>(
    calculateNewStretchIndex,
  );

  const [replacedStretch, setReplacedStretch] = useState<Stretch | undefined>();
  const [newStretch, setNewStretch] = useState<Stretch | undefined>();

  // Reset index when switching Tabs in modal
  useEffect(
    () => setNewStretchIndex(calculateNewStretchIndex),
    [trainFormPrefix],
  );

  // Set new and replaced stretch to match index
  useEffect(() => {
    if (newStretchIndex === -1) {
      setNewStretch(undefined);
      setReplacedStretch(undefined);
      setValue(originalStretchNameFieldPath, "");
      setValue(newStretchNameFieldPath, "");
      setValue(cancelledStopsFieldPath, []);
      setValue(affectedStopsFieldPath, []);
      setValue(newRouteFieldPath, []);
      return;
    }
    const _newStretch = ALTERNATIVE_STRETCHES[newStretchIndex];
    const _replacedStretch = findAlternativeStretch(_newStretch);
    setNewStretch(_newStretch);
    setReplacedStretch(_replacedStretch);
    if (!_replacedStretch) return;
    /**
     * TODO: Could consider calculating these stops at transformer.ts instead of
     * here, as they aren't displayed to the user. But it would also require
     * trainRoute, so you can be the judge as to whether it is the correct action.
     */
    const { cancelledStops, affectedStops, newRoute } = makeStretchStops(
      _newStretch,
      _replacedStretch,
      trainRoute,
      trainId,
    );
    setValue(originalStretchNameFieldPath, _replacedStretch.name);
    setValue(newStretchNameFieldPath, _newStretch.name);
    setValue(cancelledStopsFieldPath, cancelledStops);
    setValue(affectedStopsFieldPath, affectedStops);
    setValue(newRouteFieldPath, newRoute);
  }, [newStretchIndex, trainRoute]);

  const sameStretchAlreadySelected = useMemo(
    () =>
      newStretch
        ? eventAlreadyExists(newStretch, existingEvents ?? [], uuid)
        : false,
    [newStretch, existingEvents, uuid],
  );

  return (
    <Stack mb="12px">
      <Text fontWeight="bold">{t("Gammel rute:")}</Text>
      <Text pb="1rem">
        {replacedStretch
          ? formatStretchName(
              replacedStretch,
              shouldFlipStretchDirection(replacedStretch, trainRoute),
            )
          : t("Ikke valgt")}
      </Text>
      <VStack gap={0} flexBasis={1} flexGrow={1} alignItems="stretch">
        <Select
          label={t("Ny rute")}
          value={newStretchIndex}
          onChange={(e) => {
            setNewStretchIndex(Number(e.currentTarget.value));
          }}
        >
          <option value={-1}>{t("Velg en strekning")}</option>
          {ALTERNATIVE_STRETCHES.map((stretch, i) => (
            <option
              key={stretch.id}
              value={i}
              disabled={
                fetchStatus !== "error" &&
                !isRelevantStretch(stretch, trainRoute)
              }
            >
              {formatStretchName(
                stretch,
                shouldFlipStretchDirection(stretch, trainRoute),
              )}
            </option>
          ))}
        </Select>
        <RenderErrorInPath
          errors={errors}
          errorPath={newStretchNameFieldPath}
        />
      </VStack>

      {fetchStatus === "error" && (
        <Alert data-color="warning">
          {t(
            "Kunne ikke hente rutedata. Listen med omkjøringer blir ikke filtrert automatisk. Pass på at du velger riktig omkjøring.",
          )}
        </Alert>
      )}

      {sameStretchAlreadySelected && (
        <Alert data-color="warning">
          {t("Samme trasé er valgt i en annen hendelse")}
        </Alert>
      )}
    </Stack>
  );
};
